import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Compass, Calendar, User, Lock } from 'lucide-react';
import { isFeatureUnlocked } from '../services/iapService';
import useKeyboardOpen from '../hooks/useKeyboardOpen';

const navItems = [
  { id: 'home', label: 'Today', icon: Home, path: '/home', feature: null },
  { id: 'domains', label: 'Domains', icon: Compass, path: '/my-domains', feature: null },
  { id: 'timeline', label: 'Timeline', icon: Calendar, path: '/dashboard-new', feature: 'timeline' },
  { id: 'profile', label: 'Profile', icon: User, path: '/profile-new', feature: null },
];

const BottomNav = ({ active }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { open: kbOpen } = useKeyboardOpen();
  
  const getActive = () => {
    if (active) return active;
    const currentPath = location.pathname;
    const item = navItems.find(item => item.path === currentPath);
    return item?.id || 'home';
  };
  
  const activeId = getActive();
  
  return (
    <div
      data-testid="bottom-nav"
      aria-hidden={kbOpen}
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 z-50"
      style={{
        // Respect Android gesture bar / iOS home indicator so the icons
        // never render beneath the system nav area. Without this, on
        // modern Android (gesture nav) the nav's bottom slice is hidden
        // and any elements positioned above it (e.g., Coming Soon cards)
        // appear to be clipped by the navbar.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Auto-hide when soft keyboard is open so it doesn't overlap inputs
        // (Sage QA, OTP, search boxes etc). Slide down + fade for native feel.
        transform: kbOpen ? 'translateY(110%)' : 'translateY(0)',
        opacity: kbOpen ? 0 : 1,
        pointerEvents: kbOpen ? 'none' : 'auto',
        transition: 'transform 220ms ease-out, opacity 180ms ease-out',
        willChange: 'transform',
      }}
    >
      <div className="max-w-2xl mx-auto px-5">
        <div className="flex items-center justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeId === item.id;
            const locked = item.feature && !isFeatureUnlocked(item.feature);
            
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => {
                  if (locked) {
                    navigate('/payment-plans');
                  } else {
                    navigate(item.path);
                  }
                }}
                className="flex flex-col items-center gap-1 py-2 px-4 rounded-lg transition-colors"
                style={{ position: 'relative' }}
              >
                <div style={{ position: 'relative' }}>
                  <Icon className={`w-6 h-6 ${
                    locked ? 'text-stone-300' : isActive ? 'text-cosmic-600' : 'text-stone-400'
                  }`} />
                  {locked && (
                    <Lock style={{ position: 'absolute', bottom: -2, right: -4, width: 10, height: 10, color: '#d97706' }} />
                  )}
                </div>
                <span className={`text-xs font-medium ${
                  locked ? 'text-stone-300' : isActive ? 'text-cosmic-600' : 'text-stone-600'
                }`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BottomNav;